import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';
import type { ReportSection } from 'src/app/pages/audit-trail/session-detail/session-detail.component';

// ── PD Session shapes ────────────────────────────────────────────────────────

export interface PdSessionAgent {
  id: number;
  name: string;
  agent_code: string | null;
}

export interface PdSessionConversationEntry {
  role: 'ai' | 'customer';
  text: string;
  time: string;
}

export interface PdSessionAiResponsePan {
  captured_number: string | null;
  captured_name: string | null;
  date_of_birth: string | null;
  uploaded_number: string | null;
  score: number;
  matched: boolean;
  error: string | null;
  captured_image?: string | null;
  uploaded_image?: string | null;
}

export interface PdSessionAiResponseFace {
  score: number;
  matched: boolean;
  confidence: string;
  error: string | null;
  captured_image?: string | null;
  uploaded_image?: string | null;
  uploaded_source?: string | null;
  method?: string | null;
  liveness_verified?: boolean;
  liveness_score?: number;
  liveness_error?: string | null;
}

export interface PdSessionAiResponseAadhaarBack {
  address?: string | null;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  error?: string | null;
  captured_image?: string | null;
}

export interface PdSessionAiResponse {
  overall_score?: number;
  result?: string;
  summary?: string;
  flags?: string[];
  pan?: PdSessionAiResponsePan;
  face?: PdSessionAiResponseFace;
  aadhaar_back?: PdSessionAiResponseAadhaarBack | null;
  /** Flattened address from Aadhaar / address-proof OCR (audit display). */
  captured_address?: string | null;
  composite_score?: number;
  answer_scores?: Array<{ question_id: number; question: string; answer: string; score: number; reasoning: string }>;
}

export interface PdSessionMetadata {
  geo_lat?: number;
  geo_lng?: number;
  geo_accuracy?: number;
  source?: string;
  video_room_url?: string;
  video_room_name?: string;
  video_host_token?: string;
  video_guest_token?: string;
  video_expires_at?: number;
  has_video_recording?: boolean;
  video_recording_mime_type?: string;
  video_recorded_at?: string;
  [key: string]: unknown;
}

export interface VideoRoomResult {
  is_configured: boolean;
  livekit_url?: string;
  room_url?: string;
  room_name?: string;
  host_token?: string;
  guest_token?: string;
  participant_token?: string;
  expires_at?: number;
  video_provider?: 'livekit';
}

export interface PdSession {
  id: number;
  session_code: string | null;
  agent_id: number;
  tenant_id: number;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  loan_type: string | null;
  loan_amount: number | string | null;
  status: string;
  result: string | null;
  score: number | string | null;
  ai_response: PdSessionAiResponse | null;
  conversation_log: PdSessionConversationEntry[] | null;
  metadata: PdSessionMetadata | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  agent?: PdSessionAgent | null;
  /** Workflow steps that ran, in workflow order; [] when no usable flow definition is pinned. */
  sections?: ReportSection[];
}

export interface PdSessionListData {
  result: PdSession[];
  total: number;
  page: number;
  page_size: number;
}

export interface ListSessionsQuery {
  agent_id?: number;
  status?: string;
  result?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface LoginActivityUser {
  id: number;
  full_name: string | null;
  email_id: string | null;
  unique_id?: string | null;
}

export interface LoginActivity {
  id: number;
  user_id: number | null;
  user_type: string | null;
  ip_address: string | null;
  user_agent: string | null;
  browser: string | null;
  device: string | null;
  platform: string | null;
  login_timestamp: string;
  logout_timestamp: string | null;
  status: string | null;
  session_id: string | null;
  created_at: string;
  user?: LoginActivityUser | null;
}

/** One row of the tamper-evident chain: who did what to which record, and whether it worked. */
export interface AuditEvent {
  id: number;
  action: string;
  source: string;
  resource_type: string | null;
  resource_id: string | null;
  outcome: string | null;
  actor_email: string | null;
  actor_type: string | null;
  created_at: string;
  /** What the action recorded about itself — the new link's token, the version published. */
  metadata?: Record<string, unknown> | null;
  source_ip?: string | null;
  tenant_id?: number | null;
  /** Chain links, so an operator can see the entry is part of the tamper-evident sequence. */
  hash?: string | null;
  prev_hash?: string | null;
  actor_id?: number | null;
  user_agent?: string | null;
  request_id?: string | null;
  http_method?: string | null;
  http_path?: string | null;
}

export type AuditTrailEntry =
  | { kind: 'session'; sort_at: string; session: PdSession }
  | { kind: 'login'; sort_at: string; login: LoginActivity }
  | { kind: 'event'; sort_at: string; event: AuditEvent };

export interface AuditTrailListData {
  result: AuditTrailEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface ListAuditTrailQuery extends ListSessionsQuery {
  entry_type?: '' | 'session' | 'login' | 'event';
}

// ── Service ──────────────────────────────────────────────────────────────────

export interface KycReferenceImage {
  image: string | null;
  /** Which reference the capture is compared against. */
  source?: 'aadhaar_ekyc' | 'uploaded_reference';
}

@Injectable({ providedIn: 'root' })
export class AdminSessionApiService {
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  listAuditTrail(query: ListAuditTrailQuery = {}): Observable<ApiEnvelope<AuditTrailListData>> {
    let params = new HttpParams();
    if (query.agent_id != null) params = params.set('agent_id', String(query.agent_id));
    if (query.entry_type) params = params.set('entry_type', query.entry_type);
    if (query.result) params = params.set('result', query.result);
    if (query.search) params = params.set('search', query.search);
    if (query.date_from) params = params.set('date_from', query.date_from);
    if (query.date_to) params = params.set('date_to', query.date_to);
    if (query.page != null) params = params.set('page', String(query.page));
    if (query.page_size != null) params = params.set('page_size', String(query.page_size));
    return this.http.get<ApiEnvelope<AuditTrailListData>>(`${this.base}/admin/audit-trail`, { params });
  }

  getLoginActivityById(id: number): Observable<ApiEnvelope<LoginActivity>> {
    return this.http.get<ApiEnvelope<LoginActivity>>(`${this.base}/admin/audit-trail/logins/${id}`);
  }

  listSessions(query: ListSessionsQuery = {}): Observable<ApiEnvelope<PdSessionListData>> {
    let params = new HttpParams();
    if (query.agent_id != null) params = params.set('agent_id', String(query.agent_id));
    if (query.status)    params = params.set('status', query.status);
    if (query.result)    params = params.set('result', query.result);
    if (query.search)    params = params.set('search', query.search);
    if (query.date_from) params = params.set('date_from', query.date_from);
    if (query.date_to)   params = params.set('date_to', query.date_to);
    if (query.page != null)      params = params.set('page', String(query.page));
    if (query.page_size != null) params = params.set('page_size', String(query.page_size));
    return this.http.get<ApiEnvelope<PdSessionListData>>(`${this.base}/admin/sessions`, { params });
  }

  getSessionById(id: number, tenantId?: number): Observable<ApiEnvelope<PdSession>> {
    let params = new HttpParams();
    if (tenantId != null && tenantId > 0) {
      params = params.set('tenant_id', String(tenantId));
    }
    return this.http.get<ApiEnvelope<PdSession>>(`${this.base}/admin/sessions/${id}`, { params });
  }

  createVideoRoom(sessionId: number): Observable<ApiEnvelope<VideoRoomResult>> {
    return this.http.post<ApiEnvelope<VideoRoomResult>>(`${this.base}/admin/sessions/${sessionId}/video-room`, {});
  }

  /**
   * Resolve the reference image (base64) a session's capture is compared against.
   * `source` says which it is: the UIDAI e-KYC photo when the tenant stored one for
   * this session, otherwise the agent's uploaded document.
   */
  getKycReferenceImage(
    sessionId: number,
    /** DocumentType.requirement_key: 'pan', 'selfie', 'aadhaar-back', … */
    slot: string,
    tenantId?: number | null,
  ): Observable<ApiEnvelope<KycReferenceImage>> {
    return this.http.get<ApiEnvelope<KycReferenceImage>>(
      `${this.base}/admin/sessions/${sessionId}/kyc-reference/${slot}`,
      tenantId ? { params: { tenant_id: String(tenantId) } } : {},
    );
  }

  /** Re-run the captured-vs-uploaded comparison for a slot and return the refreshed session. */
  recompareSession(
    sessionId: number,
    /** DocumentType.requirement_key: 'pan', 'selfie', 'aadhaar-back', … */
    slot: string,
    tenantId?: number | null,
  ): Observable<ApiEnvelope<PdSession>> {
    return this.http.post<ApiEnvelope<PdSession>>(
      `${this.base}/admin/sessions/${sessionId}/recompare/${slot}`,
      {},
      tenantId ? { params: { tenant_id: String(tenantId) } } : {},
    );
  }

  saveSessionRecording(
    sessionId: number,
    payload: { recording?: Blob; conversation_log?: PdSessionConversationEntry[] },
  ): Observable<ApiEnvelope<PdSession>> {
    const form = new FormData();
    if (payload.recording) {
      form.append('recording', payload.recording, `session-${sessionId}.webm`);
    }
    if (payload.conversation_log?.length) {
      form.append('conversation_log', JSON.stringify(payload.conversation_log));
    }
    return this.http.post<ApiEnvelope<PdSession>>(`${this.base}/admin/sessions/${sessionId}/recording`, form);
  }

  /** Fetch saved recording as blob for inline playback. */
  getSessionRecordingBlob(sessionId: number): Observable<Blob> {
    return this.http.get(`${this.base}/admin/sessions/${sessionId}/recording`, {
      responseType: 'blob',
    });
  }
}
